import React, { useCallback, useMemo, useRef, useState } from 'react';
import { List as DxList } from 'devextreme-react/list';
import type { WorkOrderItem } from '#/graphql/types';
import { useNavigate } from 'react-router-dom';
import List from '#/common/components/List';
import {
  getWorkOrderList,
  getBuildingList,
  getLocation,
  getAssetList,
  getAsset,
} from '#/graphql/queries';
import { gql } from '@apollo/client';
import { withChip } from '#/common/utils/wrappers';
import { priorities, states } from '../../mappings/mappings';
import columns, { priorityColorMap, workOrderStateColors } from '../WorkOrderDataGrid/columns';
import { formatDate } from '#/common/utils/formatters';
import WOIconRender from './WOIconRender';
import { Filter } from '#/common/components/DataGrid';
import useSession from '#/common/hooks/useSession';
import {
  ACTION_STATE_RULES,
  BULK_EDIT_MENU_OPTIONS_MOBILE,
  BulkEditMenu,
  CROSS_ORGANIZATION_ERROR,
  determineNextState,
  getEnhancedMenuItems,
  getNotEligibleContent,
  getSuccessMessage,
  hasMultipleOrganizations,
} from '#/modules/workOrder/utils/bulk-edit';
import { UserRole } from '#/common/types/userTypes';
import ToolbarButtonPopup from '#/common/components/ToolbarButtonPopup';
import ModalWithForm from '#/common/components/ModalWithForm';
import useToast from '#/common/hooks/useToast';
import useWorkOrderBulkUpdate from '../../hooks/useWorkOrderBulkUpdate';
import { useRedirectToRelatedFindingsConfirm } from '../../hooks/useRedirectToRelatedFindingsConfirm';
import useWorkOrderDelete from '../../hooks/useWorkOrderDelete';
import { renderPriorityDot } from '#/common/utils/renderPriorityDot';
import SimplePopup from '#/common/components/SimplePopup';
import { FilterOptions } from '#/common/components/List/ListProps';
import { inUseStatuses } from '#/modules/asset/model/utils/constants';
import getPermanentFilterValue from '#/common/utils/parsers/parsePermanentFilters';

const additionalFilters = ['assignedTo.name'];

export interface WorkOrderListProps {
  hideNewAction?: boolean;
  permanentFilters?: Filter[];
}

const getFilterOptions = (userId: string | undefined): FilterOptions => ({
  title: 'Filter Work Orders',
  groups: [
    {
      id: 'assignment',
      label: 'Assignment',
      type: 'checkbox',
      dataField: 'assigned_to_id',
      dataSource: [
        {
          label: 'Assigned to Me',
          value: userId,
        },
        { label: 'Unassigned', value: null },
      ],
    },
    {
      id: 'state',
      label: 'Status',
      type: 'checkbox',
      dataField: 'state',
      dataSource: [
        {
          label: 'Open',
          value: 'open',
          customColors: workOrderStateColors.open,
        },
        {
          label: 'In Progress',
          value: 'in_progress',
          customColors: workOrderStateColors.in_progress,
        },
        {
          label: 'Pending Review',
          value: 'pending_review',
          customColors: workOrderStateColors.pending_review,
        },
        {
          label: 'Closed',
          value: 'closed',
          customColors: workOrderStateColors.closed,
        },
      ],
    },
    {
      id: 'priority',
      label: 'Priority',
      type: 'checkbox',
      dataField: 'priority',
      dataSource: [
        {
          label: 'Critical',
          value: 5,
        },
        { label: 'High', value: 4 },
        { label: 'Medium', value: 3 },
        { label: 'Low', value: 2 },
        { label: 'Very Low', value: 1 },
      ],
    },
    {
      id: 'building',
      label: 'Building',
      type: 'reference',
      dataField: 'assetLocation.id',
      dataSource: {
        getItem: gql(getLocation),
        getItemList: gql(getBuildingList),
        displayExpr: 'name',
        valueExpr: 'id',
        filter: ['inUseStatus', '=', inUseStatuses.inUse.key],
      },
    },
    {
      id: 'asset',
      label: 'Asset',
      type: 'reference',
      dataField: 'asset.assetTag',
      dataSource: {
        getItem: gql(getAsset),
        getItemList: gql(getAssetList),
        displayExpr: 'assetTag',
        valueExpr: 'assetTag',
      },
    },
  ],
});

const WorkOrderList: React.FC<WorkOrderListProps> = ({ hideNewAction, permanentFilters }) => {
  const navigate = useNavigate();
  const list = useRef<DxList | null>(null);

  const openWorkOrder = useCallback(
    (workOrder: WorkOrderItem) => {
      navigate(`/work-orders/${workOrder.id}`);
    },
    [navigate],
  );

  const { redirectToRelatedFindingsConfirm } = useRedirectToRelatedFindingsConfirm();
  const { user } = useSession();
  const role = user?.role as UserRole | undefined;
  const menuItems = BULK_EDIT_MENU_OPTIONS_MOBILE?.[role as keyof BulkEditMenu] || [];

  const { alert, confirm } = useToast();
  const { bulkUpdateWorkOrder } = useWorkOrderBulkUpdate();
  const [selectedItems, setSelectedItems] = useState<WorkOrderItem[]>([]);
  const [eligibleItems, setEligibleItems] = useState<WorkOrderItem[]>([]);
  const [showToolbarPopup, setShowToolbarPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [clearSelectedItemsFn, setClearSelectedItemsFn] = useState<(() => void) | null>(null);
  const [errorPopupContent, setErrorPopupContent] = useState<{
    title: string | null;
    message: string | null;
  }>({
    title: '',
    message: '',
  });
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const { deleteWorkOrder } = useWorkOrderDelete();

  const handleFloatingBtnOnClick = useCallback(
    (workOrders?: WorkOrderItem[], clearSelectedItems?: () => void) => {
      if (!workOrders?.length) return;

      if (hasMultipleOrganizations(workOrders)) {
        setShowErrorPopup(true);
        setErrorPopupContent(CROSS_ORGANIZATION_ERROR);
        return;
      }

      setSelectedItems(workOrders);
      setShowToolbarPopup(true);
      setClearSelectedItemsFn(() => clearSelectedItems || null);
    },
    [],
  );

  const filterOptions: FilterOptions = useMemo(() => getFilterOptions(user?.id), [user?.id]);

  const handleOnClose = useCallback(() => {
    setShowToolbarPopup(false);
  }, []);

  const handleOnSelected = useCallback(
    async (id: string, isEligible?: boolean) => {
      if (!isEligible && id !== 'delete') {
        const { title, message } = getNotEligibleContent(id);
        setShowErrorPopup(true);
        setErrorPopupContent({ title, message });
        return;
      }

      const stateRule = ACTION_STATE_RULES[id];
      const items: WorkOrderItem[] = list.current?.instance.option('items') || [];

      const selectedIds = new Set(selectedItems.map((i) => i.id));

      const eligible = items.filter((wo) => {
        if (!selectedIds.has(wo?.id ?? '')) return false;

        if ((wo?.state ?? '') === states.closed.key) return false;

        return !stateRule || stateRule(wo?.state ?? '');
      });

      if (id === 'assign_to_me') {
        if (eligible.length === 0) {
          alert('error', 'No eligible work orders to assign.');
          return;
        }

        const sure = await confirm(
          'info',
          `Are you sure you want to assign yourself to ${eligible?.length ?? 0} selected work orders?`,
        );
        if (!sure) return;

        try {
          const res = await bulkUpdateWorkOrder({
            values: { assignedToId: user?.id ?? '' },
            ids: eligible.map((wo) => wo.id) as string[],
          });

          alert('success', `${res?.updatedCount} work orders were successfully assigned to you.`);
          list.current && list.current.instance.reload();
        } catch (error) {
          console.error(error);
          alert('error', 'Bulk operation has failed.');
        }
        clearSelectedItemsFn && clearSelectedItemsFn();
        setShowToolbarPopup(false);
        return;
      }

      if (id === 'delete') {
        const deletable = selectedItems.filter((wo) => wo.state === states.open.key);

        if (!deletable?.length) {
          alert('warning', 'No deletable work orders found. Only "Open" ones can be deleted.');
          return;
        }

        if (deletable.length < selectedItems.length) {
          const sure = await confirm(
            'error',
            `Some selected work orders are not in 'Open' status and can not be deleted.`,
          );
          if (!sure) return;
        }

        const assetId = getPermanentFilterValue('assetId', permanentFilters) || undefined;

        await deleteWorkOrder(deletable.map((wo) => wo.id).join(','), assetId);

        alert(
          'success',
          `${deletable.length} work order${deletable.length > 1 ? 's' : ''} deleted.`,
        );
        list.current && list.current.instance.reload();
        clearSelectedItemsFn && clearSelectedItemsFn();
        setShowToolbarPopup(false);
        return;
      }

      setSelectedOptionId(id);
      setEligibleItems(eligible || []);
      setShowToolbarPopup(false);
    },
    [selectedItems, user],
  );

  const handleOnCloseFormPopup = useCallback(() => {
    setSelectedOptionId(null);
  }, []);

  const handleSubmit = useCallback(
    async (formData?: WorkOrderItem) => {
      if (!formData || !eligibleItems?.length) return;

      const state = determineNextState(selectedOptionId ?? '');

      try {
        const values =
          selectedOptionId === 'close_work_orders'
            ? {
                ...formData,
                state,
                closedById: user?.id ?? '',
                closedAt: formData?.closedAt ?? new Date(),
              }
            : state
              ? { ...formData, state }
              : formData;

        const res = await bulkUpdateWorkOrder({
          values,
          ids: eligibleItems.map((wo) => wo.id) as string[],
        });

        const successMessage = getSuccessMessage(selectedOptionId ?? '', res?.updatedCount ?? 0);
        list.current && list.current.instance.reload();
        handleOnCloseFormPopup();

        const relatedFindingsIds = eligibleItems
          .filter((wo) => !!wo.findingId)
          .map((wo) => wo.findingId) as string[];

        if (
          selectedOptionId === 'close_work_orders' &&
          relatedFindingsIds?.length &&
          eligibleItems?.length
        ) {
          await redirectToRelatedFindingsConfirm(relatedFindingsIds, eligibleItems.length);
        }
        {
          clearSelectedItemsFn && clearSelectedItemsFn();
          await alert('success', successMessage);
        }
      } catch (error) {
        console.error(error);
        alert('error', 'Bulk operation has failed.');
      }
    },
    [eligibleItems, selectedOptionId],
  );

  const enhancedMenuItems = getEnhancedMenuItems(menuItems, selectedItems || []);

  const selectedMenuItem = useMemo(() => {
    const item = enhancedMenuItems.find(({ id }) => id === selectedOptionId);
    return item;
  }, [enhancedMenuItems, selectedOptionId]);

  const handleSetRef = useCallback((ref?: any) => {
    list.current = ref;
  }, []);

  return (
    <>
      <List<WorkOrderItem>
        setRef={handleSetRef}
        getItemsQuery={gql(getWorkOrderList)}
        permanentFilters={permanentFilters}
        titleKey="title"
        additionalFilters={additionalFilters}
        onClick={openWorkOrder}
        subtitleKey="displayNumber"
        customChip={(item) => {
          const stateLabel = states[item.state as keyof typeof states]?.label || '';
          const stateColors = item.state ? workOrderStateColors[item.state as string] : undefined;
          return item.state ? withChip(stateLabel, 'normal', stateColors) : null;
        }}
        customHeadRight={(item) =>
          renderPriorityDot({
            item,
            priorityItems: priorities,
            priorityColorMap,
            getPriorityValue: (item) => item.priority,
          })
        }
        customIcon={(item) => <WOIconRender type={item.type} />}
        mobileLayout
        bodyFields={[
          { key: 'asset.assetTag', label: 'Asset', labelKey: 'asset' },
          { key: (item) => formatDate(item.dueDate), label: 'Due Date' },
          { key: 'assignedTo.name', label: 'Assigned To' },
        ]}
        columnList={columns}
        floatingButtonOnClick={handleFloatingBtnOnClick}
        filterOptions={filterOptions}
      />

      <ToolbarButtonPopup
        top="auto"
        left="auto"
        right={45}
        bottom={65}
        menuItems={enhancedMenuItems}
        visible={showToolbarPopup}
        onClose={handleOnClose}
        onSelected={handleOnSelected}
      />

      <ModalWithForm
        selectedMenuItem={selectedMenuItem}
        visible={!!selectedOptionId}
        onClose={handleOnCloseFormPopup}
        onSubmit={handleSubmit}
        eligibleCount={eligibleItems?.length}
        selectedCount={selectedItems?.length}
      />

      <SimplePopup
        width="100%"
        visible={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
        onSubmit={() => setShowErrorPopup(false)}
        title={errorPopupContent.title}
        message={errorPopupContent.message}
      />
    </>
  );
};

export default React.memo(WorkOrderList);
